import { Graph, Edge, type EdgeLabel } from '@antv/x6';
import { register, getProvider } from '@antv/x6-react-shape';
import DispositivoRedeGraphNode from './DispositivoRedeGraphNode';
import EnergiaRedeGraphNode from './EnergiaRedeGraphNode';
import {
  DIAGRAM_NODE_HEIGHT,
  DIAGRAM_NODE_WIDTH,
  DIAGRAM_ENERGIA_NODE_HEIGHT,
  DIAGRAM_ENERGIA_NODE_WIDTH,
  SHAPE_NAME,
  ENERGIA_SHAPE_NAME,
} from './diagramaRedeUtils';

export { SHAPE_NAME, ENERGIA_SHAPE_NAME };
export const REDE_EDGE_SHAPE = 'rede-diagram-edge';

export const ANT_LINE_ANIMATION = 'ant-line 30s infinite linear';
export const REDE_EDGE_DOWN_BLINK_CLASS = 'rede-edge-down-blink';
export const REDE_EDGE_FLOW_DURATION_MS = 2800;

export type DiagramEdgeMonitorStatus = 'online' | 'offline' | 'unknown' | 'neutral';

const OFFLINE_DOWN_LABELS = [
  {
    markup: [
      { tagName: 'rect', selector: 'body' },
      { tagName: 'text', selector: 'label' },
    ],
    attrs: {
      label: {
        text: 'DOWN',
        fill: '#ffffff',
        fontSize: 9,
        fontWeight: 700,
        fontFamily: 'Consolas, monospace',
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
        pointerEvents: 'none',
        class: REDE_EDGE_DOWN_BLINK_CLASS,
      },
      body: {
        ref: 'label',
        refWidth: '150%',
        refHeight: '180%',
        refX: '-25%',
        refY: '-40%',
        fill: '#C62828',
        stroke: '#7f0000',
        strokeWidth: 1,
        rx: 2,
        ry: 2,
        class: REDE_EDGE_DOWN_BLINK_CLASS,
        pointerEvents: 'none',
      },
    },
    position: {
      distance: 0.5,
      offset: { x: 0, y: -6 },
    },
  },
] as const;

const NEON_BALL_LABEL_BASE: Pick<EdgeLabel, 'markup' | 'attrs'> = {
  markup: [
    { tagName: 'circle', selector: 'glow' },
    { tagName: 'circle', selector: 'core' },
  ],
  attrs: {
    glow: {
      r: 8,
      fill: '#69f0ae',
      opacity: 0.5,
      pointerEvents: 'none',
      class: 'rede-edge-neon-glow',
    },
    core: {
      r: 4,
      fill: '#eeff41',
      stroke: '#ffffff',
      strokeWidth: 1,
      pointerEvents: 'none',
      class: 'rede-edge-neon-core',
    },
  },
};

export function buildNeonBallLabelAt(distance: number): EdgeLabel {
  return {
    ...NEON_BALL_LABEL_BASE,
    position: {
      distance,
      offset: { x: 0, y: 0 },
    },
  };
}

/** Aplica efeitos visuais da conexão conforme status do destino. */
export function applyEdgeVisualState(edge: Edge, status: DiagramEdgeMonitorStatus): void {
  if (status === 'offline') {
    edge.setLabels([...OFFLINE_DOWN_LABELS]);
    edge.attr('line/strokeDasharray', 0);
    edge.attr('line/style/animation', '');
    edge.attr('line/class', REDE_EDGE_DOWN_BLINK_CLASS);
    return;
  }

  edge.attr('line/strokeDasharray', 5);
  edge.attr('line/style/animation', ANT_LINE_ANIMATION);
  edge.attr('line/class', '');

  if (status === 'online') {
    edge.setLabels([buildNeonBallLabelAt(0)]);
    return;
  }

  edge.setLabels([]);
}

/** @deprecated use applyEdgeVisualState */
export function applyEdgeOfflineState(edge: Edge, isOffline: boolean): void {
  applyEdgeVisualState(edge, isOffline ? 'offline' : 'online');
}

const LINE_ANIMATION_ATTRS = {
  strokeDasharray: 5,
  style: {
    animation: ANT_LINE_ANIMATION,
  },
} as const;

let x6DiagramRegistered = false;

export function buildRedeEdgeAttrs(stroke: string, strokeWidth: number) {
  return {
    line: {
      stroke,
      strokeWidth,
      targetMarker: { name: 'block', width: 8, height: 6 },
      ...LINE_ANIMATION_ATTRS,
    },
    wrap: {
      stroke: 'transparent',
      strokeWidth: 14,
    },
  };
}

function registerRedeDiagramEdge() {
  Graph.registerEdge(
    REDE_EDGE_SHAPE,
    {
      inherit: 'edge',
      attrs: {
        line: {
          stroke: '#2196f3',
          strokeWidth: 2,
          targetMarker: { name: 'block', width: 8, height: 6 },
          ...LINE_ANIMATION_ATTRS,
        },
      },
    },
    true
  );
}

export function ensureDiagramX6Registered() {
  if (x6DiagramRegistered) return;

  register({
    shape: SHAPE_NAME,
    width: DIAGRAM_NODE_WIDTH,
    height: DIAGRAM_NODE_HEIGHT,
    component: DispositivoRedeGraphNode,
  });

  register({
    shape: ENERGIA_SHAPE_NAME,
    width: DIAGRAM_ENERGIA_NODE_WIDTH,
    height: DIAGRAM_ENERGIA_NODE_HEIGHT,
    component: EnergiaRedeGraphNode,
  });

  registerRedeDiagramEdge();
  x6DiagramRegistered = true;
}

/** @deprecated use ensureDiagramX6Registered */
export function ensureDiagramShapeRegistered() {
  ensureDiagramX6Registered();
}

/** Provider obrigatório para nós React — evita race condition no unmount/redraw (X6 #4328). */
export const X6ReactPortalProvider = getProvider();
